import React, { useState, useEffect } from 'react';
import axios from 'axios';

// Utility function to decode JWT token (for debugging)
const decodeJWT = (token) => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) throw new Error('Invalid token format');
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload;
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
};

const Student = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [availableCourses, setAvailableCourses] = useState([]);
  const [learningPaths, setLearningPaths] = useState([]);
  const [homework, setHomework] = useState([]);
  const [progress, setProgress] = useState({});
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courseVideos, setCourseVideos] = useState([]);
  const [courseHomework, setCourseHomework] = useState([]);

  // Chat functionality
  const [chatMessage, setChatMessage] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [selectedChatCourse, setSelectedChatCourse] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchEnrolledCourses();
    fetchAvailableCourses();
    fetchLearningPaths();
    fetchHomework();
  }, []);

  const fetchEnrolledCourses = async () => {
    const token = localStorage.getItem('token');
    console.log('Student: Fetching enrolled courses...');
    console.log('Student: API URL:', process.env.REACT_APP_API_BASE_URL);
    console.log('Student: Token exists:', !!token);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/enrollments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Student: Enrolled courses API response:', response);
      console.log('Student: Enrolled courses data:', response.data);
      console.log('Student: Number of enrolled courses:', response.data.length);
      setEnrolledCourses(response.data);
    } catch (error) {
      console.error('Student: Error fetching enrolled courses:', error);
      console.error('Student: Error response:', error.response?.data);
      console.error('Student: Error status:', error.response?.status);
      console.error('Student: Error message:', error.message);
      console.log('Student: Falling back to sample data due to API failure');
      // Fallback to sample data if API fails
      setEnrolledCourses([
        { id: 1, title: 'Mathematics Fundamentals', description: 'Basic mathematics concepts and problem solving', status: 'active' },
        { id: 2, title: 'Introduction to Physics', description: 'Fundamental principles of physics', status: 'active' }
      ]);
    }
  };

  const fetchAvailableCourses = async () => {
    const token = localStorage.getItem('token');
    console.log('Student: Fetching available courses...');
    console.log('Student: API URL:', process.env.REACT_APP_API_BASE_URL);
    console.log('Student: Token exists:', !!token);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/enrollments/available`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Student: Available courses response:', response.data);
      setAvailableCourses(response.data);
    } catch (error) {
      console.error('Student: Error fetching available courses:', error);
      console.error('Student: Error details:', error.response?.data || error.message);
    }
  };

  const fetchLearningPaths = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/learning`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLearningPaths(response.data);
    } catch (error) {
      console.error('Error fetching learning paths', error);
      // Fallback to sample data
      setLearningPaths([
        { id: 1, syllabus_title: 'Mathematics Fundamentals', current_topic_title: 'Algebra Basics', progress_percentage: 75 },
        { id: 2, syllabus_title: 'Introduction to Physics', current_topic_title: 'Newtonian Mechanics', progress_percentage: 50 }
      ]);
    }
  };

  const fetchHomework = async () => {
    // Mock homework data for now
    setHomework([
      { id: 1, title: 'Math Assignment 1', description: 'Complete exercises 1-10', due_date: '2025-01-15', status: 'pending' },
      { id: 2, title: 'Science Project', description: 'Research and present on renewable energy', due_date: '2025-01-20', status: 'submitted' }
    ]);
  };

  const handleEnroll = async (courseId) => {
    const token = localStorage.getItem('token');
    const role = localStorage.getItem('role');
    console.log('Student: Attempting to enroll in course:', courseId);
    console.log('Student: Current token exists:', !!token);
    console.log('Student: Current user role from localStorage:', role);
    console.log('Student: API URL:', process.env.REACT_APP_API_BASE_URL);

    // Decode and inspect JWT token
    if (token) {
      const decodedToken = decodeJWT(token);
      console.log('Student: Decoded JWT token:', decodedToken);
      if (decodedToken) {
        console.log('Student: JWT role:', decodedToken.role);
        console.log('Student: JWT user ID:', decodedToken.id);
        console.log('Student: JWT username:', decodedToken.username);
        console.log('Student: JWT expires:', decodedToken.exp ? new Date(decodedToken.exp * 1000) : 'No expiry');
      }
    }

    if (!token) {
      alert('No authentication token found. Please log in again.');
      return;
    }

    if (role !== 'student') {
      alert(`Cannot enroll: You are logged in as '${role}' but must be a 'student' to enroll in courses.`);
      return;
    }

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/enrollments`, { course_id: courseId }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Student: Enrollment successful:', response.data);
      fetchEnrolledCourses();
      fetchAvailableCourses();
      alert('Successfully enrolled in course!');
    } catch (error) {
      console.error('Student: Error enrolling:', error);
      console.error('Student: Error response:', error.response?.data);
      console.error('Student: Error status:', error.response?.status);
      alert(`Enrollment failed: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleUnenroll = async (courseId) => {
    if (window.confirm('Are you sure you want to unenroll from this course? You will lose access to all course materials.')) {
      const token = localStorage.getItem('token');
      try {
        await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/enrollments/${courseId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchEnrolledCourses();
        fetchAvailableCourses();
        alert('Successfully unenrolled from course');
      } catch (error) {
        console.error('Error unenrolling', error);
        alert('Error unenrolling from course');
      }
    }
  };

  const handleSelectCourse = async (course) => {
    setSelectedCourse(course);
    const token = localStorage.getItem('token');

    // Fetch videos for this course
    try {
      const videoResponse = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/videos/course/${course.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourseVideos(videoResponse.data);
    } catch (error) {
      console.error('Error fetching course videos', error);
      setCourseVideos([]);
    }

    // Fetch homework for this course
    try {
      const homeworkResponse = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/homework/course/${course.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourseHomework(homeworkResponse.data);
    } catch (error) {
      console.error('Error fetching course homework', error);
      setCourseHomework([]);
    }
  };

  const handleChat = async () => {
    if (!chatMessage.trim()) return;

    const token = localStorage.getItem('token');
    setIsLoading(true);

    try {
      const payload = { message: chatMessage };
      if (selectedChatCourse) {
        payload.course_id = selectedChatCourse.id;
      }

      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/chat`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const newMessage = {
        user: chatMessage,
        ai: response.data.response,
        sources: response.data.sources || [],
        timestamp: new Date().toLocaleTimeString()
      };

      setChatHistory([...chatHistory, newMessage]);
      setChatMessage('');
    } catch (error) {
      console.error('Error chatting', error);
      const errorMessage = {
        user: chatMessage,
        ai: 'Sorry, I encountered an error while processing your question. Please try again.',
        sources: [],
        timestamp: new Date().toLocaleTimeString(),
        error: true
      };
      setChatHistory([...chatHistory, errorMessage]);
      setChatMessage('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    window.location.href = '/';
  };

  return (
    <div className="container-fluid">
      {/* Dashboard Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card shadow">
            <div className="card-body d-flex justify-content-between align-items-center">
              <div>
                <h2 className="mb-1">🎓 Student Dashboard</h2>
                <p className="text-muted mb-0">Track your learning progress and access course materials</p>
              </div>
              <button className="btn btn-outline-danger" onClick={handleLogout}>
                <i className="fas fa-sign-out-alt me-2"></i>Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="row mb-4">
        <div className="col-md-3">
          <div className="card text-center shadow">
            <div className="card-body">
              <div className="stats-card">
                <h3>{enrolledCourses.length}</h3>
                <p className="mb-0">Enrolled Courses</p>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card text-center shadow">
            <div className="card-body">
              <div className="stats-card">
                <h3>78%</h3>
                <p className="mb-0">Overall Progress</p>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card text-center shadow">
            <div className="card-body">
              <div className="stats-card">
                <h3>{homework.length}</h3>
                <p className="mb-0">Assignments</p>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card text-center shadow">
            <div className="card-body">
              <div className="stats-card">
                <h3>92%</h3>
                <p className="mb-0">Average Score</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="row">
        <div className="col-12">
          <div className="card shadow">
            <div className="card-header">
              <ul className="nav nav-tabs card-header-tabs" id="studentTabs" role="tablist">
                <li className="nav-item" role="presentation">
                  <button className={`nav-link ${activeTab === 'dashboard' ? 'active' : ''}`} onClick={() => setActiveTab('dashboard')}>
                    <i className="fas fa-tachometer-alt me-2"></i>Dashboard
                  </button>
                </li>
                <li className="nav-item" role="presentation">
                  <button className={`nav-link ${activeTab === 'courses' ? 'active' : ''}`} onClick={() => setActiveTab('courses')}>
                    <i className="fas fa-book me-2"></i>My Courses
                  </button>
                </li>
                <li className="nav-item" role="presentation">
                  <button className={`nav-link ${activeTab === 'learning' ? 'active' : ''}`} onClick={() => setActiveTab('learning')}>
                    <i className="fas fa-route me-2"></i>Learning Path
                  </button>
                </li>
                <li className="nav-item" role="presentation">
                  <button className={`nav-link ${activeTab === 'progress' ? 'active' : ''}`} onClick={() => setActiveTab('progress')}>
                    <i className="fas fa-chart-line me-2"></i>Progress
                  </button>
                </li>
                <li className="nav-item" role="presentation">
                  <button className={`nav-link ${activeTab === 'homework' ? 'active' : ''}`} onClick={() => setActiveTab('homework')}>
                    <i className="fas fa-tasks me-2"></i>Homework
                  </button>
                </li>
                <li className="nav-item" role="presentation">
                  <button className={`nav-link ${activeTab === 'assistant' ? 'active' : ''}`} onClick={() => setActiveTab('assistant')}>
                    <i className="fas fa-robot me-2"></i>AI Assistant
                  </button>
                </li>
              </ul>
            </div>
            <div className="card-body">
              {/* Tab Content */}
              {activeTab === 'dashboard' && (
                <div className="fade-in">
                  <h4>Welcome to your Student Dashboard</h4>
                  <p>Access your courses, track progress, and get help from our AI assistant.</p>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="card border-primary">
                        <div className="card-body">
                          <h5 className="card-title text-primary">📚 Course Access</h5>
                          <p className="card-text">Browse and enroll in available courses.</p>
                          <button className="btn btn-primary" onClick={() => setActiveTab('courses')}>View Courses</button>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="card border-success">
                        <div className="card-body">
                          <h5 className="card-title text-success">🤖 AI Study Assistant</h5>
                          <p className="card-text">Get intelligent help with your courses using RAG technology.</p>
                          <button className="btn btn-success" onClick={() => setActiveTab('assistant')}>Start Chatting</button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'courses' && (
                <div className="fade-in">
                  <div className="row">
                    <div className="col-md-6">
                      <h4>Enrolled Courses</h4>
                      <div className="row">
                        {enrolledCourses.map(course => (
                          <div key={course.id} className="col-md-12 mb-3">
                            <div className={`card h-100 shadow-sm ${selectedCourse && selectedCourse.id === course.id ? 'border-primary' : ''}`}>
                              <div className="card-body">
                                <h6>{course.title}</h6>
                                <p>{course.description}</p>
                                <div className="d-flex justify-content-between align-items-center">
                                  <span className="badge bg-success">Enrolled</span>
                                  <small className="text-muted">Click to view content</small>
                                </div>
                                <div className="d-flex gap-2 mt-2">
                                  <button className="btn btn-outline-primary btn-sm" onClick={() => handleSelectCourse(course)}>View Course</button>
                                  <button className="btn btn-outline-danger btn-sm" onClick={(e) => { e.stopPropagation(); handleUnenroll(course.id); }}>Unenroll</button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <h4>Available Courses</h4>
                      <div className="row">
                        {availableCourses.filter(course => !enrolledCourses.some(enrolled => enrolled.id === course.id)).map(course => (
                          <div key={course.id} className="col-md-12 mb-3">
                            <div className="card h-100 shadow-sm">
                              <div className="card-body">
                                <h6>{course.title}</h6>
                                <p>{course.description}</p>
                                <button className="btn btn-primary btn-sm" onClick={() => handleEnroll(course.id)}>Enroll</button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Selected Course Content */}
                  {selectedCourse && (
                    <div className="mt-4">
                      <h4>📚 {selectedCourse.title} - Course Content</h4>
                      <div className="row">
                        <div className="col-md-6">
                          <h5>🎥 Course Videos</h5>
                          {courseVideos.length > 0 ? (
                            <div className="row">
                              {courseVideos.map(video => (
                                <div key={video.id} className="col-md-12 mb-3">
                                  <div className="card">
                                    <div className="card-body">
                                      <h6>{video.title || 'Untitled Video'}</h6>
                                      <p>{video.url}</p>
                                      <button className="btn btn-primary btn-sm">Watch Video</button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-muted">No videos available for this course yet.</p>
                          )}
                        </div>
                        <div className="col-md-6">
                          <h5>📝 Course Homework</h5>
                          {courseHomework.length > 0 ? (
                            <div className="row">
                              {courseHomework.map(hw => (
                                <div key={hw.id} className="col-md-12 mb-3">
                                  <div className="card">
                                    <div className="card-body">
                                      <h6>{hw.title}</h6>
                                      <p>{hw.description}</p>
                                      <p className="text-muted">Due: {hw.due_date}</p>
                                      <button className="btn btn-success btn-sm">Start Assignment</button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-muted">No homework available for this course yet.</p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'learning' && (
                <div className="fade-in">
                  <h4>Personalized Learning Paths</h4>
                  <div className="row">
                    {learningPaths.map(path => (
                      <div key={path.id} className="col-md-6 mb-3">
                        <div className="card">
                          <div className="card-body">
                            <h6>{path.syllabus_title}</h6>
                            <p>Current Topic: {path.current_topic_title || 'Not started'}</p>
                            <div className="progress mb-2">
                              <div className="progress-bar" style={{ width: `${path.progress_percentage}%` }}>
                                {path.progress_percentage}%
                              </div>
                            </div>
                            <button className="btn btn-outline-primary btn-sm">Continue Learning</button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'progress' && (
                <div className="fade-in">
                  <h4>Progress Tracking & Analytics</h4>
                  <div className="row">
                    <div className="col-md-3">
                      <div className="card text-center shadow">
                        <div className="card-body">
                          <div className="stats-card">
                            <h3>78%</h3>
                            <p className="mb-0">Overall Progress</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="card text-center shadow">
                        <div className="card-body">
                          <div className="stats-card">
                            <h3>92%</h3>
                            <p className="mb-0">Average Score</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="card text-center shadow">
                        <div className="card-body">
                          <div className="stats-card">
                            <h3>15</h3>
                            <p className="mb-0">Completed Topics</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="card text-center shadow">
                        <div className="card-body">
                          <div className="stats-card">
                            <h3>5</h3>
                            <p className="mb-0">Active Courses</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4">
                    <h5>Recent Activity</h5>
                    <ul className="list-group">
                      <li className="list-group-item">Completed Math Quiz - Score: 95%</li>
                      <li className="list-group-item">Finished Science Video Lecture</li>
                      <li className="list-group-item">Submitted History Essay</li>
                      <li className="list-group-item">Started New Course: Advanced Physics</li>
                    </ul>
                  </div>
                </div>
              )}

              {activeTab === 'homework' && (
                <div className="fade-in">
                  <div className="row">
                    <div className="col-md-8">
                      <h4>Homework & Assignments</h4>
                      <div className="row">
                        {homework.map(hw => (
                          <div key={hw.id} className="col-md-12 mb-3">
                            <div className="card">
                              <div className="card-body">
                                <h6>{hw.title}</h6>
                                <p>{hw.description}</p>
                                <div className="d-flex justify-content-between align-items-center">
                                  <small className="text-muted">Due: {hw.due_date}</small>
                                  <div className="d-flex gap-2">
                                    {hw.status === 'pending' ? (
                                      <>
                                        <input type="file" className="form-control form-control-sm" />
                                        <button className="btn btn-success btn-sm">Submit</button>
                                      </>
                                    ) : (
                                      <span className="badge bg-success">Submitted</span>
                                    )}
                                    <button className="btn btn-outline-info btn-sm">Download Materials</button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="col-md-4">
                      <h4>🤖 Quick AI Help</h4>
                      <div className="border rounded p-3" style={{ height: '300px', overflowY: 'scroll' }}>
                        <small className="text-muted">For detailed AI assistance, visit the AI Assistant tab.</small>
                      </div>
                      <div className="input-group mt-2">
                        <input type="text" className="form-control form-control-sm" placeholder="Quick question..." />
                        <button className="btn btn-outline-primary btn-sm" onClick={() => setActiveTab('assistant')}>
                          <i className="fas fa-robot"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'assistant' && (
                <div className="fade-in">
                  <div className="row">
                    <div className="col-md-12">
                      <div className="card">
                        <div className="card-header bg-primary text-white">
                          <h4 className="mb-0">🤖 AI Study Assistant</h4>
                          <small>Powered by RAG - searches your course materials for accurate answers</small>
                        </div>
                        <div className="card-body">
                          <div className="row">
                            <div className="col-md-4">
                              <div className="mb-3">
                                <label className="form-label fw-bold">🎯 Focus Area</label>
                                <select
                                  className="form-select"
                                  value={selectedChatCourse?.id || ''}
                                  onChange={(e) => {
                                    const course = enrolledCourses.find(c => c.id === parseInt(e.target.value));
                                    setSelectedChatCourse(course || null);
                                  }}
                                >
                                  <option value="">All enrolled courses</option>
                                  {enrolledCourses.map(course => (
                                    <option key={course.id} value={course.id}>{course.title}</option>
                                  ))}
                                </select>
                                <small className="text-muted">Select a course to focus the AI's search</small>
                              </div>

                              <div className="mb-3">
                                <label className="form-label fw-bold">💡 Suggested Questions</label>
                                <div className="d-grid gap-2">
                                  {[
                                    "Explain Newton's laws of motion",
                                    "Help me understand algebra equations",
                                    "What are the key points from this week's lecture?",
                                    "How do I solve this math problem?"
                                  ].map((question, idx) => (
                                    <button
                                      key={idx}
                                      className="btn btn-outline-secondary btn-sm text-start"
                                      onClick={() => setChatMessage(question)}
                                      disabled={isLoading}
                                    >
                                      {question}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            </div>

                            <div className="col-md-8">
                              <div className="chat-container border rounded" style={{ height: '500px', display: 'flex', flexDirection: 'column' }}>
                                <div className="chat-messages p-3" style={{ flex: 1, overflowY: 'scroll' }}>
                                  {chatHistory.length === 0 && (
                                    <div className="text-center text-muted mt-5">
                                      <i className="fas fa-robot fa-4x mb-3 text-primary"></i>
                                      <h5>Welcome to your AI Study Assistant!</h5>
                                      <p>Ask me anything about your courses. I'll search through your materials to provide accurate, contextual answers.</p>
                                      <div className="alert alert-info">
                                        <strong>How it works:</strong> I use Retrieval-Augmented Generation (RAG) to search your course content, documents, and guidelines to give you the most relevant answers.
                                      </div>
                                    </div>
                                  )}
                                  {chatHistory.map((msg, idx) => (
                                    <div key={idx} className="mb-4">
                                      <div className="d-flex justify-content-between align-items-center mb-2">
                                        <strong className="text-primary">
                                          <i className="fas fa-user me-2"></i>You
                                        </strong>
                                        <small className="text-muted">{msg.timestamp}</small>
                                      </div>
                                      <div className="mb-3 p-3 bg-light rounded">{msg.user}</div>

                                      <div className="d-flex justify-content-between align-items-center mb-2">
                                        <strong className="text-success">
                                          <i className="fas fa-robot me-2"></i>AI Assistant
                                        </strong>
                                        {msg.sources && msg.sources.length > 0 && (
                                          <small className="text-muted">
                                            <i className="fas fa-book me-1"></i>{msg.sources.length} sources found
                                          </small>
                                        )}
                                      </div>
                                      <div className={`p-3 rounded ${msg.error ? 'bg-danger text-white' : 'bg-success text-white'}`}>
                                        {msg.ai}
                                      </div>

                                      {msg.sources && msg.sources.length > 0 && (
                                        <div className="mt-3">
                                          <div className="border-start border-primary ps-3">
                                            <small className="text-muted fw-bold">📚 Sources Used:</small>
                                            <div className="mt-2">
                                              {msg.sources.map((source, sidx) => (
                                                <div key={sidx} className="small border rounded p-2 mb-2 bg-light">
                                                  <div className="text-muted">{source.text}</div>
                                                  {source.metadata && (
                                                    <div className="mt-1">
                                                      <small className="text-primary">
                                                        {source.metadata.filename && <><i className="fas fa-file me-1"></i>{source.metadata.filename}</>}
                                                        {source.metadata.title && <><i className="fas fa-tag me-1"></i>{source.metadata.title}</>}
                                                        {source.similarity && <><i className="fas fa-percentage me-1"></i>{Math.round(source.similarity * 100)}% relevance</>}
                                                      </small>
                                                    </div>
                                                  )}
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                  {isLoading && (
                                    <div className="text-center mt-3">
                                      <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                      </div>
                                      <div className="mt-2">
                                        <small className="text-muted">🔍 Searching your course materials...</small>
                                        <br />
                                        <small className="text-muted">🧠 Generating contextual answer...</small>
                                      </div>
                                    </div>
                                  )}
                                </div>

                                <div className="chat-input p-3 border-top">
                                  <div className="input-group">
                                    <input
                                      type="text"
                                      className="form-control"
                                      value={chatMessage}
                                      onChange={(e) => setChatMessage(e.target.value)}
                                      onKeyPress={(e) => e.key === 'Enter' && !isLoading && handleChat()}
                                      placeholder="Ask me anything about your courses..."
                                      disabled={isLoading}
                                    />
                                    <button
                                      className="btn btn-primary"
                                      onClick={handleChat}
                                      disabled={isLoading || !chatMessage.trim()}
                                    >
                                      {isLoading ? (
                                        <span className="spinner-border spinner-border-sm" role="status"></span>
                                      ) : (
                                        <><i className="fas fa-paper-plane me-1"></i>Send</>
                                      )}
                                    </button>
                                  </div>
                                  <div className="mt-2">
                                    <small className="text-muted">
                                      <i className="fas fa-lightbulb me-1"></i>
                                      Tip: Be specific about topics or ask for explanations of concepts from your courses
                                    </small>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Student;